import { ShopTierCompanyConfig } from "./config";

export interface ShopHistoryEntry {
  tier: string;
  date: string;
  actor: string;
  time?: string;
  action?: string;
  reason?: string;
}

export interface MockShopRecord {
  code: string;
  shop: string;
  taxId: string;
  province: string;
  zone: string;
  tier: string;
  volumeLabel: string;
  progressPercent: number;
  navSynced: boolean;
  navBy: string | null;
  navAt: string | null;
  changedDate: string;
  changedBy: string;
  history: ShopHistoryEntry[];
  isRegard: boolean;
}

export interface AuditLogEntry {
  code: string;
  shop: string;
  province: string;
  date: string;
  time: string;
  action: string;
  before: string;
  after: string;
  reason: string;
  actor: string;
}

export interface ConfigTierBenefit {
  tierKey: string;
  thresholdLabel: string;
  tierTitle: string;
  titleDescription: string;
  percent: number;
  showInApp: boolean;
  benefits: string[];
}

export interface ConfigYear {
  year: string;
  status: "active" | "closed" | 'upcoming';
  tiersCount: number;
  lastEdited: string;
  tiers: ConfigTierBenefit[];
}

export interface ShopTierMockData {
  shops: MockShopRecord[];
  auditLog: AuditLogEntry[];
  configYears: ConfigYear[];
}

const SAMPLE_SHOPS = [
  { shop: "หจก.เจริญพืชผลการเกษตร", province: "นครสวรรค์", zone: "A01" },
  { shop: "บจก.พูนผลเกษตรภัณฑ์", province: "สุพรรณบุรี", zone: "A02" },
  { shop: "ร้านรุ่งเรืองเคมีเกษตร", province: "เชียงราย", zone: "A05" },
  { shop: "ร้านสมบูรณ์การเกษตร", province: "อุบลราชธานี", zone: "MABIN-1" },
  { shop: "หจก.กิจเจริญยิ่งเกษตรภัณฑ์", province: "บุรีรัมย์", zone: "MABIN-2" },
  { shop: "บจก.ศรีอุดมปุ๋ยและเคมีเกษตร", province: "นครศรีธรรมราช", zone: "PH1" },
  { shop: "ร้านทวีผลการเกษตร", province: "กาญจนบุรี", zone: "A03" },
  { shop: "หจก.มงคลชัยการเกษตร", province: "ลพบุรี", zone: "A04" },
];

const ACTORS = ["สมชาย ใจดี", "สมหญิง แก้วมณี"];
const VOLUME_BASE_MT = [520, 1850, 4200, 7300, 10800, 13500];
const VOLUME_BASE_BAHT = [2.8, 7.2, 15.4, 24.6, 38.5, 55.2];
const BENEFIT_POOL = [
  "ส่วนลดพิเศษท้ายบิล",
  "ของสมนาคุณประจำไตรมาส",
  "สิทธิ์จองสินค้าก่อนรอบปกติ",
  "เจ้าหน้าที่ดูแลบัญชีเฉพาะ",
  "สิทธิ์เข้าร่วมทริปประจำปี",
  "วงเงินเครดิตเพิ่มพิเศษ",
];

const AUDIT_ACTION_POOL: {
  action: (config: ShopTierCompanyConfig) => string;
  before: (shop: MockShopRecord, config: ShopTierCompanyConfig, toggleOn: boolean) => string;
  after: (shop: MockShopRecord, config: ShopTierCompanyConfig, toggleOn: boolean) => string;
  reason: string;
}[] = [
  {
    action: () => "ปฏิเสธ",
    before: (shop) => shop.tier,
    after: () => "คงระดับเดิม",
    reason: "หลักฐานยอดสะสมไม่เพียงพอ",
  },
  {
    action: () => "ปรับยอด",
    before: (shop, config) => formatVolume(config, parseFloat(shop.volumeLabel.replace(/,/g, "")) * 0.95),
    after: (shop, config) => formatVolume(config, parseFloat(shop.volumeLabel.replace(/,/g, ""))),
    reason: "ยอดตกหล่นจากใบสั่งขาย",
  },
  {
    action: () => "อัปเดต Navision",
    before: () => "-",
    after: () => "-",
    reason: "คีย์ข้อมูลเข้าระบบ Navision",
  },
  {
    action: (config) => `เปลี่ยนนโยบายการแสดง % ${config.percentLabel}`,
    before: (_shop, _config, toggleOn) => (toggleOn ? "เปิด" : "ปิด"),
    after: (_shop, _config, toggleOn) => (toggleOn ? "ปิด" : "เปิด"),
    reason: "เปิด/ปิดการแสดงเปอร์เซ็นต์สิทธิประโยชน์ในแอปร้านค้า",
  },
  {
    action: () => "เปลี่ยนนโยบายการแสดงสิทธิ์ปีหน้า",
    before: (_shop, _config, toggleOn) => (toggleOn ? "เปิด" : "ปิด"),
    after: (_shop, _config, toggleOn) => (toggleOn ? "ปิด" : "เปิด"),
    reason: "เปิด/ปิดการแสดงสิทธิประโยชน์ปีถัดไปในแอปร้านค้า",
  },
];

function pad(n: number, width = 3): string {
  return String(n).padStart(width, "0");
}

function volumeLabelForTier(config: ShopTierCompanyConfig, tierIndex: number): string {
  if (config.volumeUnit === "ล้านบาท") {
    const v = VOLUME_BASE_BAHT[Math.min(tierIndex, VOLUME_BASE_BAHT.length - 1)];
    return v.toFixed(2);
  }
  const v = VOLUME_BASE_MT[Math.min(tierIndex, VOLUME_BASE_MT.length - 1)];
  return v.toLocaleString("th-TH");
}

function formatVolume(config: ShopTierCompanyConfig, value: number): string {
  const formatted = config.volumeUnit === "ล้านบาท" ? value.toFixed(2) : Math.round(value).toLocaleString("th-TH");
  return `ยอดสะสม ${formatted} ${config.volumeUnit}`;
}

function buildShops(config: ShopTierCompanyConfig): MockShopRecord[] {
  const tiers = config.tiers;
  return SAMPLE_SHOPS.map((sample, i) => {
    const tierIndex = tiers.length - 1 - (i % tiers.length);
    const tier = tiers[tierIndex];
    const prevTier = tiers[Math.max(tierIndex - 1, 0)];
    const actor = ACTORS[i % ACTORS.length];
    const synced = i % 3 !== 0;
    const history: ShopHistoryEntry[] = [
      { tier: tier.key, date: `0${(i % 9) + 1}/0${(i % 6) + 1}/2569`, actor: "ระบบ", time: "09:40 น." },
    ];
    if (tierIndex > 0) {
      history.push({ tier: prevTier.key, date: "01/01/2568", actor });
    }
    return {
      code: `CL${pad(101 + i)}`,
      shop: sample.shop,
      taxId: `0${100000000000 + i * 7654321}`.slice(0, 13),
      province: sample.province,
      zone: sample.zone,
      tier: tier.key,
      tierPredict: tier.key,
      volumeLabel: volumeLabelForTier(config, tierIndex),
      progressPercent: Math.round(((i % tiers.length) / Math.max(tiers.length - 1, 1)) * 100),
      navSynced: synced,
      navBy: synced ? actor : null,
      navAt: synced ? `2${(i % 8) + 1}/0${(i % 6) + 1}/2569 ${10 + (i % 8)}:15 น.` : null,
      changedDate: history[0].date,
      changedBy: history[0].actor,
      history,
      isRegard: Math.random() < 0.5,
    };
  });
}

function buildAuditLog(shops: MockShopRecord[], config: ShopTierCompanyConfig): AuditLogEntry[] {
  const entries: AuditLogEntry[] = [];
  shops.forEach((shop, shopIdx) => {
    shop.history.forEach((h, idx) => {
      const isFirstEver = idx === shop.history.length - 1;
      const action =
        idx !== 0 ? "อนุมัติ" : isFirstEver ? "เผยแพร่ครั้งแรก" : h.actor === "ระบบ" ? "เลื่อนระดับอัตโนมัติ" : "ปรับระดับ";
      entries.push({
        code: shop.code,
        shop: shop.shop,
        province: shop.province,
        date: h.date,
        time: h.time || "-",
        action,
        before: idx === 0 ? shop.history[1]?.tier || "-" : "-",
        after: idx === 0 ? shop.tier : h.tier,
        reason:
          action === "เผยแพร่ครั้งแรก"
            ? "เผยแพร่ระดับครั้งแรกให้ร้านค้า"
            : action === "เลื่อนระดับอัตโนมัติ"
              ? "ยอดสะสมถึงเกณฑ์ระดับถัดไป"
              : action === "ปรับระดับ"
                ? "ปรับระดับตามยอดสะสมประจำปี"
                : "ต่ออายุระดับเดิม",
        actor: h.actor,
      });
    });

    const extra = AUDIT_ACTION_POOL[shopIdx % AUDIT_ACTION_POOL.length];
    const toggleOn = shopIdx % 2 === 0;
    entries.push({
      code: shop.code,
      shop: shop.shop,
      province: shop.province,
      date: shop.changedDate,
      time: "14:05 น.",
      action: extra.action(config),
      before: extra.before(shop, config, toggleOn),
      after: extra.after(shop, config, toggleOn),
      reason: extra.reason,
      actor: "Sales Co (BO)",
    });
  });
  return entries;
}

function buildConfigYears(config: ShopTierCompanyConfig): ConfigYear[] {
  const tiersConfig = config.tiers.map((tier, i) => ({
    tierKey: tier.key,
    thresholdLabel: tier.subLabel,
    tierTitle: "Rebate Unknown",
    titleDescription: "Topup จากยอดที่ได้รับ 2026",
    percent: (i + 1) * 2,
    showInApp: i % 2 === 0,
    benefits: BENEFIT_POOL.slice(0, Math.min(2 + i, BENEFIT_POOL.length)),
  }));
  return [
    {
      year: "ปี 2570",
      status: "upcoming",
      tiersCount: config.tiers.length,
      lastEdited: "21/07/2569",
      tiers: tiersConfig,
    },
    {
      year: "ปี 2569",
      status: "active",
      tiersCount: config.tiers.length,
      lastEdited: "21/07/2569",
      tiers: tiersConfig,
    },
    {
      year: "ปี 2568",
      status: "closed",
      tiersCount: config.tiers.length,
      lastEdited: "03/01/2568",
      tiers: tiersConfig,
    },
    {
      year: "ปี 2567",
      status: "closed",
      tiersCount: config.tiers.length,
      lastEdited: "03/01/2567",
      tiers: tiersConfig,
    },
  ];
}

export function generateShopTierMockData(config: ShopTierCompanyConfig): ShopTierMockData {
  const shops = buildShops(config);
  return {
    shops,
    auditLog: buildAuditLog(shops, config),
    configYears: buildConfigYears(config),
  };
}
